import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { IMAGES } from "@/constants/images";
import { Reveal, MaskedLine, usePageMeta } from "@/components/Reveal";

const CHAPTERS = [
  {
    num: "01",
    title: "The path here",
    copy: "I came to celebrancy through a love of language, storytelling, and the power of words spoken at life's most important moments. Whether you are celebrating a wedding, welcoming a new life, or saying a final farewell to someone you love, I am here to help you find the right words, and to deliver them with care, presence, and warmth.",
  },
  {
    num: "02",
    title: "The craft",
    copy: "Every ceremony I create is written from scratch, shaped entirely around the people at its heart. I take the time to listen, to ask the right questions, and to craft something that is genuinely, unmistakably yours.",
  },
];

const WHY = [
  "Every ceremony written from scratch; no templates",
  "A deep love of Scottish language, poetry, and storytelling",
  "Calm, confident presence on the day",
  "Personal support from first conversation to final word",
  "Ceremonies shaped around the people at their heart, not a script from a drawer",
];

const About = () => {
  usePageMeta(
    "About Karen Woods | Scottish Voice and Verse Celebrancy",
    "Meet Karen Woods, a Scottish celebrant based in Alyth, Perthshire, offering beautifully personal ceremonies across Perth and Kinross and beyond."
  );

  return (
    <main data-testid="about-page">
      <section className="grain relative overflow-hidden bg-forestdeep pb-24 pt-40 text-cream lg:pb-32 lg:pt-52" data-testid="about-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-16 px-6 sm:px-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <p className="mb-6 text-[11px] uppercase tracking-[0.35em] text-gold">About</p>
            <h1 className="font-display text-5xl leading-[1.1] sm:text-6xl" data-testid="about-heading">
              <MaskedLine delay={0.2}>About Karen</MaskedLine>
            </h1>
            <Reveal delay={0.6}>
              <span className="gold-rule mt-10 block" />
              <p className="font-display mt-8 max-w-xl text-xl italic leading-relaxed text-cream/85 sm:text-2xl" data-testid="about-intro">
                Hello, I'm Karen Woods, a Scottish celebrant based in Alyth, Perthshire. I believe that no two people,
                families, or stories are the same, and your ceremony should be no different.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <Reveal delay={0.4}>
              <div className="mx-auto max-w-sm">
                <div className="overflow-hidden rounded-md shadow-[0_35px_70px_-30px_rgba(0,0,0,0.6)]">
                  <img
                    src={IMAGES.portrait}
                    alt="Karen Woods, celebrant of Scottish Voice and Verse"
                    data-testid="about-portrait"
                    className="aspect-square w-full object-cover transition-transform duration-[2s] hover:scale-105"
                  />
                </div>
                <p className="font-display mt-7 text-center text-lg italic text-gold" data-testid="about-portrait-caption">
                  Karen Woods
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-20 sm:px-12 lg:py-28" data-testid="about-chapters">
        {CHAPTERS.map((c) => (
          <Reveal key={c.num} className="mb-16 last:mb-0">
            <div className="grid gap-6 sm:grid-cols-12">
              <div className="sm:col-span-3">
                <p className="font-display text-6xl italic leading-none text-gold/60 sm:text-7xl" data-testid={`chapter-num-${c.num}`}>
                  {c.num}
                </p>
              </div>
              <div className="sm:col-span-9">
                <h3 className="font-display text-2xl text-mulberry sm:text-3xl" data-testid={`chapter-title-${c.num}`}>
                  {c.title}
                </h3>
                <span className="mt-5 block h-px w-16 bg-gold" />
                <p className="mt-6 text-sm leading-[1.9] text-slate2 sm:text-base">{c.copy}</p>
              </div>
            </div>
          </Reveal>
        ))}
        <Reveal>
          <div className="border-l-2 border-gold bg-creamdeep/50 p-7" data-testid="service-area-line">
            <p className="font-display text-xl italic text-forest">
              Based in Alyth, Perthshire. Serving couples and families across Perth and Kinross, Angus, Tayside, and
              beyond.
            </p>
          </div>
        </Reveal>
      </section>

      <section className="grain relative bg-forestdeep py-20 text-cream lg:py-28" data-testid="why-svv-section">
        <div className="mx-auto max-w-4xl px-6 sm:px-12">
          <Reveal className="mb-12 text-center">
            <p className="mb-4 text-[11px] uppercase tracking-[0.35em] text-gold">The Difference</p>
            <h2 className="font-display text-3xl sm:text-4xl" data-testid="why-svv-heading">
              Why Scottish Voice and Verse
            </h2>
          </Reveal>
          <Reveal>
            <ul className="mx-auto max-w-2xl space-y-6" data-testid="why-svv-list">
              {WHY.map((item, i) => (
                <li key={item} className="flex items-start gap-4" data-testid={`why-svv-item-${i + 1}`}>
                  <span className="mt-2.5 h-1.5 w-1.5 shrink-0 rotate-45 bg-gold" />
                  <span className="font-display text-lg italic leading-relaxed text-cream/90 sm:text-xl">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="grain relative overflow-hidden py-28 lg:py-36" data-testid="about-quote-section">
        <img src={IMAGES.book} alt="An open book of stories" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
        <div className="absolute inset-0 bg-royalpurple/70" />
        <Reveal className="relative z-10 mx-auto max-w-3xl px-6 text-center">
          <p className="font-display text-3xl italic leading-snug text-cream sm:text-4xl" data-testid="about-closing-line">
            No templates. No borrowed scripts. Just your story, told well.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta-button"
            className="group mt-10 inline-flex items-center gap-3 border border-gold/70 px-9 py-4 text-[12px] uppercase tracking-[0.25em] text-cream transition-colors duration-500 hover:bg-gold hover:text-forestdeep"
          >
            Let's talk about your ceremony
            <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
          </Link>
          <p className="mt-5 text-xs italic text-cream/70">No obligation, just a conversation.</p>
        </Reveal>
      </section>
    </main>
  );
};

export default About;
